import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, SeverityChip, StatusBadge, Confidence, SlaBadge, KevBadge, Spinner } from "@/components/Kit";

const SEVS = ["Critical", "High", "Medium", "Low", "Info"];
const STATUSES = ["Open", "In Progress", "Risk Accepted", "False Positive", "Remediated"];
const CATS = ["sca", "iac", "secrets"];

export default function Findings() {
  const { orgId } = useOrg();
  const navigate = useNavigate();
  const q0 = new URLSearchParams(useLocation().search);
  const [apps, setApps] = useState([]);
  const [filters, setFilters] = useState({
    app_id: q0.get("app_id") || "", severity: "", status: "", category: "",
    kev: false, has_fix: false, search: "",
  });
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setApps([]); setData({ items: [], total: 0, pages: 1 }); }, [orgId]);
  useEffect(() => { if (orgId) http.get("/applications", { params: { org_id: orgId } }).then((r) => setApps(r.data)); }, [orgId]);

  const load = useCallback(() => {
    if (!orgId) return;
    setLoading(true);
    const params = { org_id: orgId, page };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    http.get("/findings", { params }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [orgId, filters, page]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setPage(1); setFilters((p) => ({ ...p, [k]: v })); };
  const hasPotential = data.items.some((f) => f.confidence === "Potential");
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const bulk = async (status) => {
    let note = "";
    if (status === "Risk Accepted") {
      note = window.prompt("Justification note (required for Risk Accepted):") || "";
      if (!note.trim()) return;
    }
    try {
      await http.post("/findings/bulk-update", { org_id: orgId, ids: selected, status, note });
      toast.success(`${selected.length} findings updated`); setSelected([]); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto fade-up">
      <h1 className="font-display text-3xl font-bold text-[#A5300F] mb-1">Findings</h1>
      <p className="text-sm text-[#8A8175] mb-4">{data.total} findings match the current filters.</p>

      {hasPotential && (
        <div className="flex items-center gap-2 bg-[#FFF5EB] border border-[#E7D9C6] text-[#8a5a12] text-sm rounded-md px-4 py-2.5 mb-4" data-testid="potential-banner">
          <AlertTriangle className="w-4 h-4" /> Some findings are unvalidated and require manual confirmation before remediation.
        </div>
      )}

      <div className="flex gap-5">
        {/* Filter rail */}
        <div className="w-56 shrink-0 space-y-4">
          <Card className="p-4 space-y-4">
            <input placeholder="Search…" data-testid="filter-search" value={filters.search}
              onChange={(e) => setF("search", e.target.value)}
              className="w-full bg-white border border-[#E7D9C6] rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-[#A5300F]" />
            <FilterGroup label="Application">
              <select value={filters.app_id} onChange={(e) => setF("app_id", e.target.value)} className={selCls} data-testid="filter-app">
                <option value="">All</option>{apps.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </FilterGroup>
            <FilterGroup label="Severity">
              <select value={filters.severity} onChange={(e) => setF("severity", e.target.value)} className={selCls} data-testid="filter-severity">
                <option value="">All</option>{SEVS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </FilterGroup>
            <FilterGroup label="Status">
              <select value={filters.status} onChange={(e) => setF("status", e.target.value)} className={selCls} data-testid="filter-status">
                <option value="">All</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </FilterGroup>
            <FilterGroup label="Category">
              <select value={filters.category} onChange={(e) => setF("category", e.target.value)} className={selCls} data-testid="filter-category">
                <option value="">All</option>{CATS.map((s) => <option key={s}>{s.toUpperCase()}</option>)}
              </select>
            </FilterGroup>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={filters.kev} onChange={(e) => setF("kev", e.target.checked)} className="accent-[#A5300F]" data-testid="filter-kev" /> KEV only</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={filters.has_fix} onChange={(e) => setF("has_fix", e.target.checked)} className="accent-[#A5300F]" data-testid="filter-hasfix" /> Has fix available</label>
          </Card>
        </div>

        {/* Table */}
        <div className="flex-1 min-w-0">
          {selected.length > 0 && (
            <div className="flex items-center gap-2 mb-3 bg-white border border-[#ECE6DD] rounded-md px-3 py-2 text-sm" data-testid="bulk-bar">
              <span className="font-semibold">{selected.length} selected</span>
              {["In Progress", "Risk Accepted", "False Positive", "Remediated"].map((s) => (
                <button key={s} onClick={() => bulk(s)} data-testid={`bulk-${s.replace(/\s/g, "-")}`}
                  className="text-xs border border-[#E7D9C6] px-2 py-1 rounded hover:bg-[#FBF7F1]">{s}</button>
              ))}
            </div>
          )}
          <Card className="overflow-hidden">
            {loading && <div className="p-3 text-center text-[#B5AC9E] text-sm"><Spinner /> Loading…</div>}
            <table className="w-full text-sm" data-testid="findings-table">
              <thead><tr className="text-[10px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
                <th className="py-2 px-2 w-6"></th><th className="w-6"></th><th>Severity</th><th>Title</th>
                <th>Package / File</th><th>CVE</th><th className="text-right">EPSS</th><th>Conf.</th>
                <th className="text-right">Age</th><th>SLA</th><th>Status</th>
              </tr></thead>
              <tbody>
                {data.items.map((f) => (
                  <React.Fragment key={f.id}>
                    <tr className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1]" data-testid={`finding-${f.id}`}>
                      <td className="px-2"><input type="checkbox" checked={selected.includes(f.id)} onChange={() => toggleSel(f.id)} className="accent-[#A5300F]" data-testid={`select-${f.id}`} /></td>
                      <td><button onClick={() => setExpanded(expanded === f.id ? null : f.id)} data-testid={`expand-${f.id}`}>
                        {expanded === f.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button></td>
                      <td><SeverityChip severity={f.severity} small /></td>
                      <td className="font-semibold max-w-xs truncate">{f.title}</td>
                      <td className="text-[#5C554C] text-xs max-w-[180px] truncate">{f.package_name || f.file_path || "—"}</td>
                      <td className="text-xs font-mono text-[#5C554C]">{(f.cve_ids || [])[0] || "—"}</td>
                      <td className="text-right text-xs font-mono">{f.epss_score != null ? `${(f.epss_score * 100).toFixed(1)}%` : "—"}</td>
                      <td><Confidence label={f.confidence} /></td>
                      <td className="text-right text-xs">{f.age_days}d</td>
                      <td><SlaBadge status={f.sla_status} /></td>
                      <td className="flex items-center gap-1 py-2">{f.kev && <KevBadge />}<StatusBadge status={f.status} /></td>
                    </tr>
                    {expanded === f.id && (
                      <tr className="bg-[#FDFBF8]"><td colSpan={11} className="px-6 py-4">
                        <div className="grid grid-cols-3 gap-6 text-sm">
                          <div className="col-span-2">
                            <div className="text-[11px] uppercase text-[#8A8175] font-semibold mb-1">Description</div>
                            <p className="text-[#4a453d]">{f.description || "No description."}</p>
                            {f.evidence && <>
                              <div className="text-[11px] uppercase text-[#8A8175] font-semibold mt-3 mb-1">Evidence</div>
                              <pre className="bg-[#F6F1EA] border border-[#ECE6DD] rounded p-2 text-xs font-mono overflow-x-auto whitespace-pre-wrap">{f.evidence}</pre>
                            </>}
                          </div>
                          <div className="space-y-1 text-xs">
                            <KV k="Fixed version" v={f.fixed_version || "None available"} />
                            <KV k="CVSS" v={f.cvss_score ?? "—"} />
                            <KV k="CWE" v={(f.cwe_ids || []).join(", ") || "—"} />
                            <KV k="Ecosystem" v={f.package_ecosystem || "—"} />
                            <KV k="Reopened" v={f.reopened_count} />
                            <button onClick={() => navigate(`/findings/${f.id}`)} data-testid={`triage-${f.id}`}
                              className="mt-2 text-[#A5300F] font-semibold underline">Open triage →</button>
                          </div>
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
                {data.items.length === 0 && !loading && <tr><td colSpan={11} className="py-10 text-center text-[#B5AC9E]">No findings match.</td></tr>}
              </tbody>
            </table>
          </Card>
          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-4 text-sm">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border border-[#E7D9C6] rounded disabled:opacity-40">Prev</button>
              <span>Page {page} of {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="px-3 py-1 border border-[#E7D9C6] rounded disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const selCls = "w-full bg-white border border-[#E7D9C6] rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[#A5300F]";
const FilterGroup = ({ label, children }) => (
  <div><div className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold mb-1">{label}</div>{children}</div>
);
const KV = ({ k, v }) => (
  <div className="flex justify-between gap-2"><span className="text-[#8A8175]">{k}</span><span className="font-mono text-right">{String(v)}</span></div>
);
